"use client";

import { useState, useEffect } from "react";
import "../../styles/AlertaNotificacao.css";

/**
 * Componente de notificação com suporte a cores padrão:
 * - erro (vermelho)
 * - aviso (amarelo)
 * - sucesso (verde)
 * - info (azul)
 */
export const AlertaNotificacao = ({
  tipo = "info",
  mensagem = "",
  onClose = null,
  autoClose = true,
  duracao = 5000,
  className = "",
}) => {
  const [visivel, setVisivel] = useState(true);
  const [animarSaida, setAnimarSaida] = useState(false);

  useEffect(() => {
    if (!autoClose) return;

    const timer = setTimeout(() => {
      setAnimarSaida(true);
      setTimeout(() => {
        setVisivel(false);
        if (onClose) onClose();
      }, 300);
    }, duracao);

    return () => clearTimeout(timer);
  }, [autoClose, duracao, onClose]);

  const handleClose = () => {
    setAnimarSaida(true);
    setTimeout(() => {
      setVisivel(false);
      if (onClose) onClose();
    }, 300);
  };

  if (!visivel) return null;

  const getIcon = () => {
    switch (tipo) {
      case "erro":
        return "❌";
      case "aviso":
        return "⚠️";
      case "sucesso":
        return "✅";
      case "info":
        return "ℹ️";
      default:
        return "ℹ️";
    }
  };

  return (
    <div
      className={`alerta-notificacao alerta-${tipo} ${
        animarSaida ? "saindo" : ""
      } ${className}`}
    >
      <div className="alerta-conteudo">
        <span className="alerta-icon">{getIcon()}</span>
        <span className="alerta-mensagem">{mensagem}</span>
        <button className="alerta-fechar" onClick={handleClose}>
          ✕
        </button>
      </div>
    </div>
  );
};

/**
 * Container para gerenciar múltiplas notificações
 */
export const ContainerNotificacoes = ({ notificacoes = [], onRemove = null }) => {
  return (
    <div className="container-notificacoes">
      {notificacoes.map((notif) => (
        <AlertaNotificacao
          key={notif.id}
          tipo={notif.tipo}
          mensagem={notif.mensagem}
          autoClose={notif.autoClose !== false}
          duracao={notif.duracao || 5000}
          onClose={() => {
            if (onRemove) onRemove(notif.id);
          }}
        />
      ))}
    </div>
  );
};

/**
 * Hook customizado para gerenciar notificações
 */
export const useNotificacao = () => {
  const [notificacoes, setNotificacoes] = useState([]);

  const adicionar = (tipo, mensagem, opcoes = {}) => {
    const id = Date.now();
    const novaNotificacao = {
      id,
      tipo,
      mensagem,
      autoClose: opcoes.autoClose !== false,
      duracao: opcoes.duracao || 5000,
    };

    setNotificacoes((prev) => [...prev, novaNotificacao]);

    // Auto-remover após a duração
    if (novaNotificacao.autoClose) {
      setTimeout(() => {
        remover(id);
      }, novaNotificacao.duracao + 300);
    }

    return id;
  };

  const remover = (id) => {
    setNotificacoes((prev) => prev.filter((n) => n.id !== id));
  };

  const erro = (mensagem, opcoes = {}) =>
    adicionar("erro", mensagem, opcoes);
  const aviso = (mensagem, opcoes = {}) =>
    adicionar("aviso", mensagem, opcoes);
  const sucesso = (mensagem, opcoes = {}) =>
    adicionar("sucesso", mensagem, opcoes);
  const info = (mensagem, opcoes = {}) =>
    adicionar("info", mensagem, opcoes);

  const limpar = () => setNotificacoes([]);

  return {
    notificacoes,
    adicionar,
    remover,
    erro,
    aviso,
    sucesso,
    info,
    limpar,
  };
};
